import { Button, Table } from "antd";
import { setAppLoading } from "app/slices/appSlice";
import { formatNumber, isNumber, toNumber } from "helpers";
import { useEffect, useMemo, useState } from "react";
import CreateAudienceModal from "components/CreateAudienceModal";
import { useQuery } from "react-query";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

export default function TablePagination(props) {

    const dispatch = useDispatch();

    const { apiCallBack, propsInput, whereCondition, dataFields, dataTableChange } = props;

    const { data, refetch, isLoading } = useQuery(
        ['data', whereCondition, dataFields],
        async () => {
            dispatch(setAppLoading(true));

            try {
                const newWhereCondition = { ...whereCondition, ...dataFields };
                const response = await apiCallBack(Object.assign({}, newWhereCondition ? newWhereCondition : {}));
                return response;
            } catch (error) {
                throw error;
            } finally {
                dispatch(setAppLoading(false));
            }
        },
        {
            refetchOnWindowFocus: false,
            retry: false,
        }
    );

    const { dataViewTable, dataExport, fixedColumns } = useMemo(
        () => {

            if (!data || !data?.data?.statistics?.[0]) {
                return { dataViewTable: [], fixedColumns: [] };
            }

            let tmp = [];

            data?.data?.statistics.forEach((statistic, i) => {
                const value = statistic.reduce((acc, item) => {
                    acc[item.name] = formatNumber(item.value);
                    acc.type = item.type;
                    acc.id = item.id;
                    acc.key = i;
                    return acc;
                }, {});
                tmp.push(value);
            });

            return { dataViewTable: tmp, dataExport: tmp, fixedColumns: data?.data?.total || [] };
        },
        [data]
    );

    function isSortable(type, id) {
        if (type == 'dimension' && id != 1) return false;
        return true;
    }

    function setSorter(a, b, name) {
        if (isNumber(a?.[name])) {
            return toNumber(a?.[name]) - toNumber(b?.[name]);
        }

        const regex = /^(0?[1-9]|[12][0-9]|3[01])\/(0?[1-9]|1[0-2])\/(\d{4})$/;

        if (regex.test(a?.[name])) {
            const [aDay, aMonth, aYear] = a?.[name].split('/').map(Number);
            const [bDay, bMonth, bYear] = b?.[name].split('/').map(Number);
            return new Date(aYear, aMonth - 1, aDay) - new Date(bYear, bMonth - 1, bDay);
        }
        return false;
    }

    const { columns, colSpan } = useMemo(() => {

        if (!dataViewTable || !data?.data?.statistics?.[0]) {
            return { columns: [], colSpan: 0 };
        }

        const columns = data?.data?.statistics?.[0].map((item, index) => {
            const { name, type, id } = data?.data?.statistics[0][index];
            return {
                title: item?.name || "Name",
                dataIndex: item?.name,
                key: index,
                width: 150,
                value: item?.value,
                sorter: isSortable(type, id) ? (a, b) => setSorter(a, b, name) : undefined,
            }
        });

        const colSpan = data?.data?.statistics[0].filter(item => item.type == "dimension")?.length || 0;

        return { colSpan: colSpan, columns: columns };

    }, [dataViewTable]);


    useEffect(() => {
        if (!dataExport) {
            return;
        }
        dataTableChange({ data: dataExport, fixedColumns: fixedColumns, colSpan: colSpan });
    }, [dataExport]);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [isProcessingAudience, setIsProcessingAudience] = useState(false);

    const handleCreateAudience = async (name, description) => {
        setIsCreating(true);
        try {
            const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : {};

            // Ensure metricIds includes 3 (Số người mua)
            const currentMetricIds = dataFields?.metricIds || [];
            const metricIds = currentMetricIds.includes(3) ? currentMetricIds : [...currentMetricIds, 3];

            const payload = {
                ...whereCondition,
                ...dataFields,
                metricIds: metricIds,
                create_audience: 1,
                audience_name: name,
                description: description,
                userId: user?.id
            };

            const res = await apiCallBack(payload);

            if (res && res.code === 200) {
                toast.success("Hệ thống đang tạo tập khách hàng. Vui lòng kiểm tra lại sau ít phút.");
                setIsModalVisible(false);
                setIsProcessingAudience(true);
            } else {
                if (res?.message === 'SOMETHING_IS_RUNNING') {
                    toast.error("Hệ thống đang xử lý một yêu cầu tạo tập khác. Vui lòng thử lại sau.");
                } else {
                    toast.error(res?.message || "Có lỗi xảy ra");
                }
            }
        } catch (e) {
            console.error(e);
            toast.error("Có lỗi xảy ra");
        } finally {
            setIsCreating(false);
        }
    }

    return (
        <>
            <Table
                {...propsInput}
                bordered
                pagination={{
                    defaultPageSize: 20,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                }}
                columns={columns}
                dataSource={dataViewTable}
                scroll={{ x: 'calc(100vh)' }}
                loading={isLoading}
                summary={() => (
                    <Table.Summary fixed>
                        <Table.Summary.Row>
                            <Table.Summary.Cell index={0} colSpan={colSpan}>
                                <b>Tổng</b>
                            </Table.Summary.Cell>
                            {fixedColumns.map((item, index) => {
                                return (
                                    <Table.Summary.Cell key={index} index={index + 1}>
                                        <div className="flex justify-between items-center w-full">
                                            <b className="whitespace-nowrap">{formatNumber(item.value)}</b>
                                            {props.allowCreateAudience && item.id === 3 && (
                                                <Button
                                                    type="primary"
                                                    size="small"
                                                    onClick={() => setIsModalVisible(true)}
                                                    className="ml-2"
                                                    disabled={isProcessingAudience}
                                                    loading={isProcessingAudience}
                                                >
                                                    {isProcessingAudience ? "Đang tạo..." : "Tạo tập KH"}
                                                </Button>
                                            )}
                                        </div>
                                    </Table.Summary.Cell>
                                );
                            })}
                        </Table.Summary.Row>
                    </Table.Summary>
                )}
                onChange={(pagination, filters, sorter, currentDataSource) => {

                    if (currentDataSource) {
                        dataTableChange({ data: currentDataSource.currentDataSource, fixedColumns: fixedColumns, colSpan: colSpan });
                    }
                }}
            />
            <CreateAudienceModal
                visible={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onCreate={handleCreateAudience}
                loading={isCreating}
            />
        </>
    )
}